import type {UITask} from '../../types/task'
import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {TaskListLayout} from './TaskListLayout'
import {TaskActionProviders} from './TaskActionProviders'
import {ArchiveErrorBanner} from '../ArchiveErrorBanner'
import styles from './TaskListView.module.css'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

interface TaskListHeaderProps {
  activeTab: 'active' | 'completed'
  setActiveTab: (tab: 'active' | 'completed') => void
  openCount?: number
  closedCount?: number
  showSkeletonLoaders: boolean
  additionalFilters?: ReactNode
}

function TaskListHeader({
  activeTab,
  setActiveTab,
  openCount,
  closedCount,
  showSkeletonLoaders,
  additionalFilters,
}: TaskListHeaderProps) {
  return (
    <div className={styles.headerContainer}>
      <ul className={clsx(styles.tabsContainer, 'list-style-none')}>
        <li>
          <ListViewSectionFilterLink
            title="Active"
            isSelected={activeTab === 'active'}
            count={openCount?.toLocaleString()}
            isLoading={showSkeletonLoaders}
            href="#"
            onClick={e => {
              e.preventDefault()
              setActiveTab('active')
            }}
          />
        </li>
        <li>
          <ListViewSectionFilterLink
            title="Completed"
            isSelected={activeTab === 'completed'}
            count={closedCount?.toLocaleString()}
            isLoading={showSkeletonLoaders}
            href="#"
            onClick={e => {
              e.preventDefault()
              setActiveTab('completed')
            }}
          />
        </li>
      </ul>
      {additionalFilters != null && <div className={styles.filterContainer}>{additionalFilters}</div>}
    </div>
  )
}

export interface TaskListViewProps {
  tasks: UITask[]
  isError: boolean
  isLoading: boolean
  isFetching: boolean
  activeTab: 'active' | 'completed'
  setActiveTab: (tab: 'active' | 'completed') => void
  hasNextPage: boolean
  openCount?: number
  closedCount?: number
  emptyHeading: string
  emptyDescription: ReactNode
  errorText?: string
  additionalFilters?: ReactNode
  renderItem: (task: UITask) => ReactNode
}

export function TaskListView({
  tasks,
  isError,
  isLoading,
  isFetching,
  activeTab,
  setActiveTab,
  hasNextPage,
  openCount,
  closedCount,
  emptyHeading,
  emptyDescription,
  errorText = 'Unable to load tasks, try again later.',
  additionalFilters,
  renderItem,
}: TaskListViewProps) {
  const showSkeletonLoaders = isLoading || (isFetching && tasks.length === 0)

  return (
    <TaskActionProviders>
      <div className={styles.bannerContainer}>
        <ArchiveErrorBanner />
      </div>
      <TaskListLayout
        tasks={tasks}
        isError={isError}
        isLoading={isLoading}
        showSkeletonLoaders={showSkeletonLoaders}
        hasNextPage={hasNextPage}
        header={
          <TaskListHeader
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            openCount={openCount}
            closedCount={closedCount}
            showSkeletonLoaders={showSkeletonLoaders}
            additionalFilters={additionalFilters}
          />
        }
        emptyHeading={emptyHeading}
        emptyDescription={emptyDescription}
        errorText={errorText}
        renderItem={renderItem}
      />
    </TaskActionProviders>
  )
}
